import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Database, Info } from "@phosphor-icons/react";

/**
 * DataSourcesSection — transparent "Sources Used" block.
 * Reads the canonical catalogue from GET /api/data-sources.
 * Displayed at the bottom of every institutional deliverable (assessment,
 * investment committee, exports).
 */
export default function DataSourcesSection({ compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/data-sources");
        setData(data);
      } catch { /* graceful degradation */ }
    })();
  }, []);

  if (!data) return null;
  const primary = data.sources.filter((s) => s.primary);
  const others = data.sources.filter((s) => !s.primary);

  if (compact) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="data-sources-compact">
        Sources: {primary.map((s) => s.name).join(" · ")}
      </div>
    );
  }

  return (
    <section className="bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev" aria-labelledby="sources-title" data-testid="data-sources-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-teal-soft text-teal grid place-items-center">
          <Database size={18} weight="duotone" />
        </div>
        <div>
          <div className="text-overline text-primary">Transparency</div>
          <h3 id="sources-title" className="font-display text-xl tracking-tight">Sources Used</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {primary.map((s) => (
          <div key={s.key} className="flex items-start gap-3 rounded-lg border border-border p-3.5 bg-background" data-testid={`source-${s.key}`}>
            <div className="h-2 w-2 rounded-full bg-teal mt-1.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-sm hover:text-primary hover:underline">{s.name}</a>
                ) : (
                  <span className="font-semibold text-sm">{s.name}</span>
                )}
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.category}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.coverage}</div>
            </div>
          </div>
        ))}
      </div>

      {others.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-overline text-muted-foreground mb-3">Complementary sources</div>
          <div className="flex flex-wrap gap-2">
            {others.map((s) => (
              <span key={s.key} className="text-[11px] px-2.5 py-1 rounded-full bg-secondary border border-border" data-testid={`source-secondary-${s.key}`}>{s.name}</span>
            ))}
          </div>
        </div>
      )}

      <p className="mt-6 pt-5 border-t border-border text-xs text-muted-foreground leading-relaxed flex items-start gap-2">
        <Info size={12} weight="bold" className="mt-0.5 flex-shrink-0" />
        <span>{data.methodology_note}</span>
      </p>
    </section>
  );
}
